import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BrowserProvider,
  Contract,
  JsonRpcProvider,
  formatUnits,
} from "ethers";

import {
  useAppKitAccount,
  useAppKitProvider,
  useAppKitNetwork,
} from "@reown/appkit/react";


/* =========================================================
   CONFIG
========================================================= */

const BSC_CHAIN_ID = 56;

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

const CONTRACT_ADDRESS =
  process.env.REACT_APP_KIND_RUSH_CONTRACT_ADDRESS ||
  "0x900ADA53B342ebdF097465cefDD65Ac13Cb928f6";


/* =========================================================
   ABI
========================================================= */

const ABI = [
  "function getActiveCampaignId() view returns (uint256)",

  `function getGlobalView(uint256 campaignId)
    view
    returns (
      (
        uint256 campaignId,
        uint256 totalParticipants,
        uint256 totalQualified,
        uint256 totalCompleted,
        uint256 totalDisqualified,
        uint256 totalVerifiedTrades,
        uint256 totalKindBought,
        uint256 totalTradeVolumeUsd,
        uint256 totalQualifyingUsd,
        uint256 totalRewardUsd,
        uint256 totalClaimableRewardUsd,
        uint256 totalClaimedKind,
        uint256 rewardPoolKind,
        uint256 tier100Count,
        uint256 tier150Count,
        uint256 tier200Count,
        uint256 tier300Count,
        uint256 kindPriceUsd,
        uint256 startTime,
        uint256 endTime,
        uint256 claimEndTime,
        uint256 qualificationPeriod,
        uint256 claimCooldown,
        uint256 minPurchaseUsd,
        uint256 maxPurchaseUsd,
        uint256 claimCount,
        bool activated,
        bool closed
      ) v
    )`,

  `function getUserView(
      uint256 campaignId,
      address wallet
    )
    view
    returns (
      (
        uint8 status,
        uint8 reason,
        uint256 firstPurchaseTime,
        uint256 qualificationEnd,
        uint256 qualifyingPurchaseUsd,
        uint256 frozenPurchaseUsd,
        uint256 tradeVolumeUsd,
        uint256 rewardBps,
        uint256 rewardUsd,
        uint256 claimedKind,
        uint256 claimsCompleted,
        uint256 claimCount,
        uint256 tradeCount,
        uint256 nextClaimTime,
        uint256 timeUntilNextClaim,
        uint256 nextClaimUsd,
        uint256 nextClaimKind,
        uint256 currentKindBalance,
        bool qualificationFinished,
        bool claimReady
      ) v
    )`,

  "function getCompactKindPrice(uint256 campaignId) view returns (uint256)",

  "function claim(uint256 campaignId)",
];


/* =========================================================
   CONSTANTS
========================================================= */

const ZERO = 0n;

const STATUS = {
  0: "Not Registered",
  1: "Qualifying",
  2: "Active",
  3: "Completed",
  4: "Disqualified",
};

const REASON = {
  0: "None",
  1: "KIND Sold",
  2: "KIND Sent",
  3: "KIND Received",
  4: "Invalid Trade",
  5: "Unauthorized Contract",
  6: "Admin Emergency",
};


/* =========================================================
   HELPERS
========================================================= */

const usd = (
  value,
  digits = 2
) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        minimumFractionDigits:
          digits,
        maximumFractionDigits:
          digits,
      }
    );
  }
  catch {
    return "0.00";
  }
};


const token = (
  value,
  digits = 2
) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        maximumFractionDigits:
          digits,
      }
    );
  }
  catch {
    return "0";
  }
};


const kindUsdValue = (
  kindAmount,
  kindPriceUsd
) => {
  try {
    const amount = Number(
      formatUnits(
        kindAmount ?? ZERO,
        18
      )
    );

    const price = Number(
      formatUnits(
        kindPriceUsd ?? ZERO,
        18
      )
    );

    const value =
      amount * price;

    if (
      !Number.isFinite(value)
    ) {
      return "0.00";
    }

    return value.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  }
  catch {
    return "0.00";
  }
};


const shortAddress = (
  address
) => {
  if (!address) {
    return "—";
  }

  return `${address.slice(
    0,
    6
  )}...${address.slice(-4)}`;
};


const duration = (
  seconds
) => {
  let total =
    Math.max(
      0,
      Number(
        seconds || 0
      )
    );

  const days =
    Math.floor(
      total / 86400
    );

  total %= 86400;

  const hours =
    Math.floor(
      total / 3600
    );

  total %= 3600;

  const minutes =
    Math.floor(
      total / 60
    );

  const secs =
    Math.floor(
      total % 60
    );

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  }

  return `${String(
    hours
  ).padStart(
    2,
    "0"
  )}:${String(
    minutes
  ).padStart(
    2,
    "0"
  )}:${String(
    secs
  ).padStart(
    2,
    "0"
  )}`;
};


const dateText = (
  timestamp
) => {
  const value =
    Number(
      timestamp || 0
    );

  if (!value) {
    return "—";
  }

  return new Date(
    value * 1000
  ).toLocaleString();
};


const errorMessage = (
  error
) => {
  return (
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    "Transaction failed"
  );
};


/* =========================================================
   PROJECTED TIER
========================================================= */

const projectedTierFromUsd = (
  qualifyingUsd
) => {
  const amount =
    qualifyingUsd ?? ZERO;

  const USD_50 =
    50n *
    10n ** 18n;

  const USD_100 =
    100n *
    10n ** 18n;

  const USD_150 =
    150n *
    10n ** 18n;

  const USD_200 =
    200n *
    10n ** 18n;

  if (
    amount <= ZERO
  ) {
    return {
      percent: 0,
      bps: 0n,
      label: "No Tier",
      range: "$0",
    };
  }

  if (
    amount <= USD_50
  ) {
    return {
      percent: 100,
      bps: 10000n,
      label: "100%",
      range: "$1 – $50",
    };
  }

  if (
    amount <= USD_100
  ) {
    return {
      percent: 150,
      bps: 15000n,
      label: "150%",
      range: "$50.01 – $100",
    };
  }

  if (
    amount <= USD_150
  ) {
    return {
      percent: 200,
      bps: 20000n,
      label: "200%",
      range: "$100.01 – $150",
    };
  }

  if (
    amount <= USD_200
  ) {
    return {
      percent: 300,
      bps: 30000n,
      label: "300%",
      range: "$150.01 – $200",
    };
  }

  return {
    percent: 300,
    bps: 30000n,
    label: "300%",
    range: "$150.01 – $200",
  };
};


/* =========================================================
   COMPONENT
========================================================= */

export default function Rewards() {
  const {
    address,
    isConnected,
  } = useAppKitAccount();

  const {
    walletProvider,
  } = useAppKitProvider(
    "eip155"
  );

  const {
    chainId,
  } = useAppKitNetwork();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    campaignId,
    setCampaignId,
  ] = useState(ZERO);

  const [
    globalData,
    setGlobalData,
  ] = useState(null);

  const [
    userData,
    setUserData,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    claimLoading,
    setClaimLoading,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    now,
    setNow,
  ] = useState(
    Math.floor(
      Date.now() / 1000
    )
  );


  /* =======================================================
     PROVIDER
  ======================================================= */

  const readProvider =
    useMemo(
      () => {
        return new JsonRpcProvider(
          BSC_RPC
        );
      },
      []
    );


  const readContract =
    useMemo(
      () => {
        return new Contract(
          CONTRACT_ADDRESS,
          ABI,
          readProvider
        );
      },
      [
        readProvider,
      ]
    );


  /* =======================================================
     LOCAL TIMER
  ======================================================= */

  useEffect(
    () => {
      const timer =
        setInterval(
          () => {
            setNow(
              Math.floor(
                Date.now() /
                1000
              )
            );
          },
          1000
        );

      return () =>
        clearInterval(
          timer
        );
    },
    []
  );


  /* =======================================================
     LOAD DATA
  ======================================================= */

  const loadData =
    useCallback(
      async (
        showLoader = false
      ) => {
        try {
          if (showLoader) {
            setLoading(
              true
            );
          }

          setMessage("");

          const id =
            await readContract
              .getActiveCampaignId();

          setCampaignId(
            id
          );

          if (
            id === ZERO
          ) {
            setGlobalData(
              null
            );

            setUserData(
              null
            );

            return;
          }

          const global =
            await readContract
              .getGlobalView(
                id
              );

          setGlobalData(
            global
          );

          if (
            address
          ) {
            const user =
              await readContract
                .getUserView(
                  id,
                  address
                );

            setUserData(
              user
            );
          }
          else {
            setUserData(
              null
            );
          }
        }
        catch (error) {
          console.error(
            "Reward page load error:",
            error
          );

          setMessage(
            errorMessage(
              error
            )
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        readContract,
        address,
      ]
    );


  useEffect(
    () => {
      loadData(
        true
      );

      const timer =
        setInterval(
          () => {
            loadData(
              false
            );
          },
          12000
        );

      return () =>
        clearInterval(
          timer
        );
    },
    [
      loadData,
    ]
  );


  /* =======================================================
     STATUS FLAGS
  ======================================================= */

  const status =
    Number(
      userData?.status ?? 0
    );

  const reason =
    Number(
      userData?.reason ?? 0
    );

  const isDisqualified =
    status === 4;

  const isCompleted =
    status === 3;

  const isQualifying =
    status === 1;

  const isActive =
    status === 2;


  /* =======================================================
     QUALIFICATION TIMER
  ======================================================= */

  const qualificationRemaining =
    useMemo(
      () => {
        if (
          !userData ||
          isDisqualified ||
          isCompleted
        ) {
          return 0;
        }

        const end =
          Number(
            userData
              .qualificationEnd ||
            0
          );

        return Math.max(
          0,
          end - now
        );
      },
      [
        userData,
        now,
        isDisqualified,
        isCompleted,
      ]
    );


  /* =======================================================
     NEXT CLAIM TIMER
  ======================================================= */

  const nextClaimRemaining =
    useMemo(
      () => {
        if (
          !userData ||
          isDisqualified ||
          isCompleted
        ) {
          return 0;
        }

        const claims =
          Number(
            userData
              .claimsCompleted ||
            0
          );

        if (
          claims === 0
        ) {
          return qualificationRemaining;
        }

        const next =
          Number(
            userData
              .nextClaimTime ||
            0
          );

        return Math.max(
          0,
          next - now
        );
      },
      [
        userData,
        qualificationRemaining,
        now,
        isDisqualified,
        isCompleted,
      ]
    );


  /* =======================================================
     PROJECTED TIER
  ======================================================= */

  const projectedTier =
    useMemo(
      () => {
        if (!userData) {
          return projectedTierFromUsd(
            ZERO
          );
        }

        return projectedTierFromUsd(
          userData
            .qualifyingPurchaseUsd
        );
      },
      [
        userData,
      ]
    );


  /* =======================================================
     DISPLAY TIER
  ======================================================= */

  const displayTier =
    useMemo(
      () => {
        if (!userData) {
          return {
            percent: 0,
            label: "—",
            range: "—",
          };
        }

        if (
          isDisqualified
        ) {
          return {
            percent: 0,
            label:
              "Disqualified",
            range:
              "Reward unavailable",
          };
        }

        const rewardBps =
          Number(
            userData.rewardBps ||
            0
          );

        if (
          rewardBps > 0
        ) {
          const percent =
            rewardBps / 100;

          return {
            percent,
            label:
              `${percent.toFixed(
                0
              )}%`,
            range:
              projectedTier.range,
          };
        }

        return projectedTier;
      },
      [
        userData,
        isDisqualified,
        projectedTier,
      ]
    );


  /* =======================================================
     PROJECTED TOTAL REWARD
  ======================================================= */

  const projectedRewardUsd =
    useMemo(
      () => {
        if (
          !userData ||
          isDisqualified
        ) {
          return ZERO;
        }

        if (
          userData.rewardUsd >
          ZERO
        ) {
          return userData
            .rewardUsd;
        }

        const qualifying =
          userData
            .qualifyingPurchaseUsd ??
          ZERO;

        return (
          qualifying *
          projectedTier.bps
        ) /
          10000n;
      },
      [
        userData,
        projectedTier,
        isDisqualified,
      ]
    );


  /* =======================================================
     PROJECTED NEXT CLAIM USD
  ======================================================= */

const projectedNextClaimUsd =
  useMemo(
    () => {
      if (
        !userData ||
        isDisqualified ||
        isCompleted
      ) {
        return ZERO;
      }

      if (
        userData.nextClaimUsd >
        ZERO
      ) {
        return userData.nextClaimUsd;
      }

      const divisor =
        userData.claimCount ||
        4n;

      if (
        divisor <= ZERO
      ) {
        return ZERO;
      }

      return (
        projectedRewardUsd /
        divisor
      );
    },
    [
      userData,
      projectedRewardUsd,
      isDisqualified,
      isCompleted,
    ]
  );

  /* =======================================================
     PROJECTED KIND
  ======================================================= */

  const projectedNextClaimKind =
    useMemo(
      () => {
        if (
          !globalData ||
          isDisqualified ||
          isCompleted
        ) {
          return ZERO;
        }

        if (
          userData?.nextClaimKind >
          ZERO
        ) {
          return userData
            .nextClaimKind;
        }

        const price =
          globalData
            .kindPriceUsd ??
          ZERO;

        if (
          price <= ZERO ||
          projectedNextClaimUsd <=
          ZERO
        ) {
          return ZERO;
        }

        return (
          projectedNextClaimUsd *
          10n ** 18n
        ) /
          price;
      },
      [
        globalData,
        userData,
        projectedNextClaimUsd,
        isDisqualified,
        isCompleted,
      ]
    );


  /* =======================================================
     CLAIM READY
  ======================================================= */

  const claimReady =
    useMemo(
      () => {
        if (
          !userData ||
          !globalData ||
          isDisqualified ||
          isCompleted ||
          globalData.closed
        ) {
          return false;
        }

        return Boolean(
          userData.claimReady
        );
      },
      [
        userData,
        globalData,
        isDisqualified,
        isCompleted,
      ]
    );


  /* =======================================================
     CLAIM TEXT
  ======================================================= */

  const claimStateText =
    useMemo(
      () => {
        if (
          isDisqualified
        ) {
          return "CLAIM UNAVAILABLE";
        }

        if (
          isCompleted
        ) {
          return "ALL CLAIMS COMPLETED";
        }

        if (
          claimReady
        ) {
          return "CLAIM READY";
        }

        if (
          Number(
            userData
              ?.claimsCompleted ||
            0
          ) === 0
        ) {
          return duration(
            qualificationRemaining
          );
        }

        return duration(
          nextClaimRemaining
        );
      },
      [
        isDisqualified,
        isCompleted,
        claimReady,
        userData,
        qualificationRemaining,
        nextClaimRemaining,
      ]
    );


  /* =======================================================
     CLAIM
  ======================================================= */

  const handleClaim =
    useCallback(
      async () => {
        if (
          !walletProvider ||
          !address ||
          campaignId === ZERO
        ) {
          setMessage(
            "Connect your wallet first."
          );

          return;
        }

        if (
          isDisqualified
        ) {
          setMessage(
            "This wallet is disqualified and cannot claim."
          );

          return;
        }

        if (
          isCompleted
        ) {
          setMessage(
            "All reward claims are already completed."
          );

          return;
        }

        try {
          setClaimLoading(
            true
          );

          setMessage("");

          const normalizedChainId =
            String(
              chainId || ""
            ).includes(":")
              ? Number(
                  String(
                    chainId
                  )
                    .split(":")
                    .pop()
                )
              : Number(
                  chainId
                );

          if (
            normalizedChainId !==
            BSC_CHAIN_ID
          ) {
            setMessage(
              "Please switch to BSC Testnet."
            );

            return;
          }

          const provider =
            new BrowserProvider(
              walletProvider
            );

          const signer =
            await provider
              .getSigner();

          const contract =
            new Contract(
              CONTRACT_ADDRESS,
              ABI,
              signer
            );

          const tx =
            await contract.claim(
              campaignId
            );

          setMessage(
            "Claim submitted..."
          );

          await tx.wait();

          setMessage(
            "Reward claimed successfully."
          );

          await loadData(
            false
          );
        }
        catch (error) {
          console.error(
            error
          );

          setMessage(
            errorMessage(
              error
            )
          );
        }
        finally {
          setClaimLoading(
            false
          );
        }
      },
      [
        walletProvider,
        address,
        campaignId,
        chainId,
        isDisqualified,
        isCompleted,
        loadData,
      ]
    );


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyCard}>
          Loading rewards...
        </div>
      </div>
    );
  }


  /* =======================================================
     NO CAMPAIGN
  ======================================================= */

  if (
    campaignId === ZERO ||
    !globalData
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyCard}>
          <div style={styles.emptyIcon}>
            🎁
          </div>

          <h2>
            No Active Campaign
          </h2>

          <p>
            There is currently no open
            reward campaign.
          </p>
        </div>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HEADER */}

        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>
              KIND CAT TOKEN
            </div>

            <h1 style={styles.title}>
              Rewards
            </h1>

            <div style={styles.subtitle}>
               Kind Bonus Rush
              {" • "}
              Campaign #
              {campaignId.toString()}
            </div>
          </div>

          <div style={styles.headerRight}>
            <div style={styles.liveBadge}>
              <span
                style={{
                  ...styles.liveDot,
                  ...(globalData.closed
                    ? styles.closedDot
                    : {}),
                }}
              />

              {globalData.closed
                ? "Closed"
                : "Live"}
            </div>

            <button
              style={styles.refreshButton}
              onClick={() =>
                loadData(
                  true
                )
              }
            >
              Refresh
            </button>
          </div>
        </div>


        {/* CONNECT WALLET */}

        {!isConnected ||
        !address ? (
          <div style={styles.mainCard}>
            <div style={styles.emptyWallet}>
              <div style={styles.rewardIcon}>
                🎁
              </div>

              <h2>
                Connect Your Wallet
              </h2>

              <p>
                Connect the wallet that
                participated in the
                 Kind Bonus Rush
                to view and claim your
                rewards.
              </p>
            </div>
          </div>
        ) : !userData ||
          status === 0 ? (
          <div style={styles.mainCard}>
            <div style={styles.emptyWallet}>
              <div style={styles.rewardIcon}>
                ⚡
              </div>

              <h2>
                No Participation Found
              </h2>

              <p>
                This wallet has not
                participated in the
                current campaign.
              </p>

              <div style={styles.walletChip}>
                {shortAddress(
                  address
                )}
              </div>
            </div>
          </div>
        ) : (
          <>

            {/* STATUS BANNER */}

            {isDisqualified && (
              <div style={styles.disqualifiedBanner}>
                <div style={styles.bannerIcon}>
                  ✕
                </div>

                <div>
                  <div style={styles.bannerTitle}>
                    Wallet Disqualified
                  </div>

                  <div style={styles.bannerText}>
                    Reason:{" "}
                    {
                      REASON[
                        reason
                      ]
                    }
                  </div>

                  <div style={styles.bannerSub}>
                    This wallet is no
                    longer eligible for
                    rewards or claims in
                    this campaign.
                  </div>
                </div>
              </div>
            )}


            {isCompleted && (
              <div style={styles.completedBanner}>
                <div style={styles.bannerIcon}>
                  ✓
                </div>

                <div>
                  <div style={styles.bannerTitle}>
                    Rewards Completed
                  </div>

                  <div style={styles.bannerText}>
                    All 4 reward claims
                    have been completed.
                  </div>
                </div>
              </div>
            )}


            {/* TOP REWARD CARD */}

            <div style={styles.heroGrid}>

              <div style={styles.tierCard}>
                <div style={styles.cardEyebrow}>
                  CURRENT REWARD TIER
                </div>

                <div
                  style={{
                    ...styles.tierPercent,
                    ...(isDisqualified
                      ? styles.badText
                      : {}),
                  }}
                >
                  {displayTier.label}
                </div>

               

                {!isDisqualified &&
                  !isCompleted && (
                    <div style={styles.tierHint}>
                      Based on $
                      {usd(
                        userData
                          .qualifyingPurchaseUsd
                      )}{" "}
                      qualifying purchase
                    </div>
                  )}
              </div>


              <div style={styles.rewardTotalCard}>
                <div style={styles.cardEyebrow}>
                  TOTAL REWARD
                </div>

                <div style={styles.rewardTotal}>
                  $
                  {usd(
                    isDisqualified
                      ? ZERO
                      : projectedRewardUsd
                  )}
                </div>

                <div style={styles.rewardTotalSub}>
                  {isDisqualified
                    ? "Reward forfeited"
                    : userData.rewardUsd >
                      ZERO
                    ? "Final reward assigned"
                    : "Projected reward"}
                </div>
              </div>

            </div>


            {/* REWARD INFO */}

            <div style={styles.infoGrid}>

              <InfoCard
                label="Wallet"
                value={shortAddress(
                  address
                )}
              />

              <InfoCard
                label="Status"
                value={
                  STATUS[
                    status
                  ]
                }
                bad={
                  isDisqualified
                }
              />

              <InfoCard
                label="Qualifying Purchase"
                value={`$${usd(
                  userData
                    .qualifyingPurchaseUsd
                )}`}
              />

              <InfoCard
                label="Trade Volume"
                value={`$${usd(
                  userData
                    .tradeVolumeUsd
                )}`}
              />

              <InfoCard
                label="Claims Completed"
                value={`${Number(
                  userData
                    .claimsCompleted
                )} / ${Number(
                  userData
                    .claimCount
                )}`}
              />

              <InfoCard
                label="Claimed KIND"
                value={`${token(
                  userData
                    .claimedKind
                )} KIND ($${kindUsdValue(
                  userData
                    .claimedKind,
                  globalData
                    .kindPriceUsd
                )})`}
              />

            </div>


            {/* CLAIM PROGRESS */}

            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <div>
                  <div style={styles.sectionEyebrow}>
                    CLAIMS
                  </div>

                  <h2 style={styles.sectionTitle}>
                    Claim Progress
                  </h2>
                </div>

                <div style={styles.progressCount}>
                  {Number(
                    userData
                      .claimsCompleted
                  )}
                  {" / "}
                  {Number(
                    userData
                      .claimCount
                  )}
                </div>
              </div>


              <ClaimProgress
                completed={
                  Number(
                    userData
                      .claimsCompleted
                  )
                }
                total={
                  Number(
                    userData
                      .claimCount ||
                    4
                  )
                }
                disabled={
                  isDisqualified
                }
              />
            </section>


            {/* QUALIFICATION */}

            {!isCompleted && (
              <section style={styles.section}>
                <div style={styles.sectionHeader}>
                  <div>
                    <div style={styles.sectionEyebrow}>
                      QUALIFICATION
                    </div>

                    <h2 style={styles.sectionTitle}>
                      Qualification Status
                    </h2>
                  </div>
                </div>


                {isDisqualified ? (
                  <div style={styles.stoppedBox}>
                    <div style={styles.stoppedTitle}>
                      Qualification Stopped
                    </div>

                    <div style={styles.stoppedText}>
                      This wallet was
                      disqualified due to{" "}
                      <strong>
                        {
                          REASON[
                            reason
                          ]
                        }
                      </strong>
                      .
                    </div>
                  </div>
                ) : (
                  <div style={styles.qualificationGrid}>

                    <div style={styles.timerBox}>
                      <div style={styles.cardEyebrow}>
                        {Number(
                          userData
                            .claimsCompleted
                        ) === 0
                          ? "QUALIFICATION / FIRST CLAIM"
                          : "NEXT CLAIM"}
                      </div>

                      <div style={styles.timerValue}>
                        {claimStateText}
                      </div>

                      {Number(
                        userData
                          .claimsCompleted
                      ) === 0 && (
                        <div style={styles.timerMeta}>
                          Qualification
                          ends:{" "}
                          {dateText(
                            userData
                              .qualificationEnd
                          )}
                        </div>
                      )}

                      {Number(
                        userData
                          .claimsCompleted
                      ) > 0 &&
                        userData
                          .nextClaimTime >
                          ZERO && (
                          <div style={styles.timerMeta}>
                            Next claim:{" "}
                            {dateText(
                              userData
                                .nextClaimTime
                            )}
                          </div>
                        )}
                    </div>


                    <div style={styles.qualifyingStatus}>
                      <div style={styles.cardEyebrow}>
                        QUALIFICATION STATUS
                      </div>

                      <div style={styles.qualStatusValue}>
                        {isActive
                          ? "Qualified"
                          : isQualifying &&
                            qualificationRemaining >
                              0
                          ? "In Progress"
                          : isQualifying
                          ? "Qualification Complete"
                          : STATUS[
                              status
                            ]}
                      </div>

                      <div style={styles.qualSub}>
                        {isQualifying &&
                        qualificationRemaining >
                          0
                          ? "Continue holding KIND until qualification completes."
                          : "Your reward eligibility is determined by the smart contract."}
                      </div>
                    </div>

                  </div>
                )}
              </section>
            )}


            {/* NEXT CLAIM */}

            <section style={styles.claimSection}>

              <div style={styles.claimLeft}>
                <div style={styles.sectionEyebrow}>
                  NEXT REWARD
                </div>

                <div style={styles.nextRewardUsd}>
                  $
                  {usd(
                    isDisqualified ||
                    isCompleted
                      ? ZERO
                      : projectedNextClaimUsd
                  )}
                </div>

                <div style={styles.nextRewardKind}>
                  {token(
                    isDisqualified ||
                    isCompleted
                      ? ZERO
                      : projectedNextClaimKind
                  )}{" "}
                  KIND{" "}
                  <span style={styles.kindUsdSmall}>
                    ($
                    {kindUsdValue(
                      isDisqualified ||
                        isCompleted
                        ? ZERO
                        : projectedNextClaimKind,
                      globalData
                        .kindPriceUsd
                    )}
                    )
                  </span>
                </div>

                {!isDisqualified &&
                  !isCompleted &&
                  userData
                    .rewardUsd ===
                    ZERO && (
                    <div style={styles.estimateLabel}>
                      Estimated using
                      current KIND price
                    </div>
                  )}
              </div>


              <div style={styles.claimMiddle}>
                <div style={styles.cardEyebrow}>
                  KIND PRICE
                </div>

                <div style={styles.priceValue}>
                  $
                  {usd(
                    globalData
                      .kindPriceUsd,
                    8
                  )}
                </div>
              </div>


              <div style={styles.claimRight}>
                <button
                  onClick={
                    handleClaim
                  }
                  disabled={
                    !claimReady ||
                    claimLoading ||
                    isDisqualified ||
                    isCompleted
                  }
                  style={{
                    ...styles.claimButton,

                    ...(!claimReady ||
                    claimLoading ||
                    isDisqualified ||
                    isCompleted
                      ? styles.claimButtonDisabled
                      : {}),
                  }}
                >
                  {claimLoading
                    ? "Claiming..."
                    : isDisqualified
                    ? "Claim Unavailable"
                    : isCompleted
                    ? "Claims Completed"
                    : claimReady
                    ? "Claim Reward"
                    : "Not Ready"}
                </button>

                <div style={styles.claimButtonInfo}>
                  {isDisqualified
                    ? "Disqualified wallets cannot claim."
                    : isCompleted
                    ? "All rewards have been claimed."
                    : claimReady
                    ? "Your reward is ready to claim."
                    : "Claim will unlock automatically when eligible."}
                </div>
              </div>

            </section>


            {/* REWARD SUMMARY */}

            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <div>
                  <div style={styles.sectionEyebrow}>
                    SUMMARY
                  </div>

                  <h2 style={styles.sectionTitle}>
                    Reward Details
                  </h2>
                </div>
              </div>


              <div style={styles.summaryGrid}>
                <InfoCard
                  label="Reward Tier"
                  value={
                    isDisqualified
                      ? "Unavailable"
                      : displayTier.label
                  }
                  bad={
                    isDisqualified
                  }
                />

                <InfoCard
                  label="Total Reward"
                  value={`$${usd(
                    isDisqualified
                      ? ZERO
                      : projectedRewardUsd
                  )}`}
                />

                <InfoCard
                  label="Next Claim USD"
                  value={`$${usd(
                    isDisqualified ||
                    isCompleted
                      ? ZERO
                      : projectedNextClaimUsd
                  )}`}
                />

                <InfoCard
                  label="Next Claim KIND"
                  value={`${token(
                    isDisqualified ||
                    isCompleted
                      ? ZERO
                      : projectedNextClaimKind
                  )} KIND ($${kindUsdValue(
                    isDisqualified ||
                      isCompleted
                      ? ZERO
                      : projectedNextClaimKind,
                    globalData
                      .kindPriceUsd
                  )})`}
                />

                <InfoCard
                  label="Wallet KIND"
                  value={`${token(
                    userData
                      .currentKindBalance
                  )} KIND ($${kindUsdValue(
                    userData
                      .currentKindBalance,
                    globalData
                      .kindPriceUsd
                  )})`}
                />

                <InfoCard
                  label="Verified Trades"
                  value={Number(
                    userData
                      .tradeCount
                  ).toLocaleString()}
                />

                <InfoCard
                  label="Claim Deadline"
                  value={dateText(
                    globalData
                      .claimEndTime
                  )}
                />

                <InfoCard
                  label="Disqualification"
                  value={
                    REASON[
                      reason
                    ]
                  }
                  bad={
                    isDisqualified
                  }
                />
              </div>
            </section>

          </>
        )}


        {message && (
          <div style={styles.message}>
            {message}
          </div>
        )}

      </div>
    </div>
  );
}


/* =========================================================
   SMALL COMPONENTS
========================================================= */

function InfoCard({
  label,
  value,
  bad = false,
}) {
  return (
    <div style={styles.infoCard}>
      <div style={styles.cardEyebrow}>
        {label}
      </div>

      <div
        style={{
          ...styles.infoValue,
          ...(bad
            ? styles.badText
            : {}),
        }}
      >
        {value}
      </div>
    </div>
  );
}


function ClaimProgress({
  completed,
  total,
  disabled,
}) {
  const steps =
    Array.from(
      {
        length:
          total || 4,
      },
      (_, index) =>
        index + 1
    );

  return (
    <div style={styles.progressWrap}>
      {steps.map(
        (
          step,
          index
        ) => {
          const done =
            step <= completed;

          return (
            <React.Fragment
              key={step}
            >
              <div style={styles.progressStepWrap}>
                <div
                  style={{
                    ...styles.progressCircle,

                    ...(done
                      ? styles.progressCircleDone
                      : {}),

                    ...(disabled
                      ? styles.progressCircleDisabled
                      : {}),
                  }}
                >
                  {done
                    ? "✓"
                    : step}
                </div>

                <div style={styles.progressLabel}>
                  Claim {step}
                </div>
              </div>

              {index <
                steps.length -
                  1 && (
                <div
                  style={{
                    ...styles.progressLine,

                    ...(step <=
                    completed
                      ? styles.progressLineDone
                      : {}),

                    ...(disabled
                      ? styles.progressLineDisabled
                      : {}),
                  }}
                />
              )}
            </React.Fragment>
          );
        }
      )}
    </div>
  );
}


/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight: "100vh",

    background:
      "radial-gradient(circle at top left, rgba(255,204,102,.18), transparent 34%), radial-gradient(circle at top right, rgba(229,173,69,.08), transparent 28%), #080a0f",

    color: "#ffffff",

    padding: "30px 18px 80px",

    fontFamily: "Inter, Arial, sans-serif",
  },

  container: {
    maxWidth: "1280px",
    margin: "0 auto",
  },

  header: {
    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    gap: "20px",

    flexWrap: "wrap",

    marginBottom: "24px",
  },

  eyebrow: {
    color: "#ffcc66",

    fontSize: "11px",

    fontWeight: 900,

    letterSpacing: "2px",
  },

  title: {
    margin: "7px 0 0",

    fontSize: "40px",

    lineHeight: 1,

    fontWeight: 900,
  },

  subtitle: {
    color: "#7c8190",

    fontSize: "13px",

    marginTop: "11px",
  },

  headerRight: {
    display: "flex",

    alignItems: "center",

    gap: "10px",

    flexWrap: "wrap",
  },

  liveBadge: {
    display: "flex",

    alignItems: "center",

    gap: "8px",

    padding: "11px 15px",

    borderRadius: "13px",

    background: "#11151d",

    border: "1px solid #252a36",

    fontSize: "12px",

    fontWeight: 800,
  },

  liveDot: {
    width: "8px",

    height: "8px",

    borderRadius: "50%",

    background: "#69ed9d",

    boxShadow: "0 0 12px rgba(105,237,157,.8)",
  },

  closedDot: {
    background: "#ff7288",

    boxShadow: "0 0 12px rgba(255,114,136,.5)",
  },

  refreshButton: {
    border: 0,

    borderRadius: "13px",

    padding: "12px 18px",

    cursor: "pointer",

    color: "#ffffff",

    fontWeight: 800,

    background: "linear-gradient(135deg,#ffcc66,#e5ad45)",
  },

  mainCard: {
    background: "linear-gradient(145deg,#11151d,#0c0f15)",

    border: "1px solid #222735",

    borderRadius: "24px",

    padding: "35px",

    minHeight: "340px",
  },

  emptyWallet: {
    minHeight: "280px",

    display: "flex",

    flexDirection: "column",

    alignItems: "center",

    justifyContent: "center",

    textAlign: "center",

    color: "#848a99",
  },

  rewardIcon: {
    width: "70px",

    height: "70px",

    borderRadius: "22px",

    display: "grid",

    placeItems: "center",

    fontSize: "31px",

    background: "linear-gradient(145deg,#1d1810,#2a2112)",

    border: "1px solid #5a4523",

    marginBottom: "14px",
  },

  walletChip: {
    marginTop: "15px",

    background: "#171b24",

    border: "1px solid #292f3c",

    borderRadius: "11px",

    padding: "9px 14px",

    fontFamily: "monospace",

    color: "#c7cad3",

    overflowWrap: "anywhere",
  },

  disqualifiedBanner: {
    display: "flex",

    gap: "15px",

    alignItems: "center",

    padding: "20px",

    borderRadius: "18px",

    marginBottom: "16px",

    background:
      "linear-gradient(120deg,rgba(255,70,95,.15),rgba(20,13,17,.95))",

    border: "1px solid rgba(255,103,127,.35)",
  },

  completedBanner: {
    display: "flex",

    gap: "15px",

    alignItems: "center",

    padding: "20px",

    borderRadius: "18px",

    marginBottom: "16px",

    background:
      "linear-gradient(120deg,rgba(60,226,133,.13),rgba(10,18,14,.95))",

    border: "1px solid rgba(93,230,155,.3)",
  },

  bannerIcon: {
    width: "44px",

    height: "44px",

    flexShrink: 0,

    display: "grid",

    placeItems: "center",

    borderRadius: "13px",

    background: "rgba(255,255,255,.06)",

    fontSize: "20px",

    fontWeight: 900,
  },

  bannerTitle: {
    fontSize: "17px",

    fontWeight: 850,
  },

  bannerText: {
    marginTop: "4px",

    fontSize: "13px",

    color: "#d2d4db",
  },

  bannerSub: {
    marginTop: "4px",

    fontSize: "11px",

    color: "#9297a4",
  },

  heroGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",

    gap: "14px",
  },

  tierCard: {
    minHeight: "180px",

    borderRadius: "23px",

    padding: "25px",

    background:
      "linear-gradient(135deg,rgba(255,204,102,.24),rgba(17,20,28,.98))",

    border: "1px solid rgba(255,204,102,.43)",

    boxShadow: "0 18px 55px rgba(0,0,0,.2)",

    minWidth: 0,
  },

  rewardTotalCard: {
    minHeight: "180px",

    borderRadius: "23px",

    padding: "25px",

    background: "linear-gradient(145deg,#11151d,#0d1016)",

    border: "1px solid #232936",

    boxShadow: "0 18px 55px rgba(0,0,0,.2)",

    minWidth: 0,
  },

  cardEyebrow: {
    textTransform: "uppercase",

    color: "#777e8c",

    fontSize: "10px",

    fontWeight: 850,

    letterSpacing: "1.2px",
  },

  tierPercent: {
    fontSize: "54px",

    lineHeight: 1,

    fontWeight: 950,

    marginTop: "18px",
  },

  tierRange: {
    marginTop: "10px",

    color: "#d8bd85",

    fontSize: "14px",

    fontWeight: 700,
  },

  tierHint: {
    marginTop: "16px",

    color: "#777e8c",

    fontSize: "11px",
  },

  rewardTotal: {
    fontSize: "48px",

    lineHeight: 1,

    fontWeight: 950,

    marginTop: "22px",
  },

  rewardTotalSub: {
    color: "#777e8c",

    marginTop: "12px",

    fontSize: "12px",
  },

  infoGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",

    gap: "10px",

    marginTop: "14px",
  },

  infoCard: {
    background: "#0c0f15",

    border: "1px solid #1e232e",

    borderRadius: "15px",

    padding: "16px",

    minWidth: 0,
  },

  infoValue: {
    marginTop: "8px",

    fontSize: "15px",

    fontWeight: 800,

    overflowWrap: "anywhere",
  },

  badText: {
    color: "#ff7187",
  },

  section: {
    marginTop: "16px",

    padding: "22px",

    borderRadius: "21px",

    background:
      "linear-gradient(145deg,rgba(17,20,28,.97),rgba(10,13,19,.97))",

    border: "1px solid #202531",
  },

  sectionHeader: {
    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    gap: "15px",

    flexWrap: "wrap",

    marginBottom: "20px",
  },

  sectionEyebrow: {
    color: "#ffcc66",

    fontSize: "10px",

    letterSpacing: "1.5px",

    fontWeight: 900,
  },

  sectionTitle: {
    margin: "5px 0 0",

    fontSize: "21px",
  },

  progressCount: {
    padding: "9px 13px",

    borderRadius: "11px",

    background: "#171b25",

    color: "#a0a5b1",

    fontSize: "12px",

    fontWeight: 800,
  },

  progressWrap: {
    display: "flex",

    alignItems: "flex-start",

    justifyContent: "center",

    width: "100%",

    overflowX: "auto",

    padding: "15px 5px 5px",

    WebkitOverflowScrolling: "touch",
  },

  progressStepWrap: {
    minWidth: "75px",

    display: "flex",

    flexDirection: "column",

    alignItems: "center",
  },

  progressCircle: {
    width: "42px",

    height: "42px",

    borderRadius: "50%",

    display: "grid",

    placeItems: "center",

    background: "#11151d",

    border: "2px solid #303644",

    color: "#79808f",

    fontWeight: 900,
  },

  progressCircleDone: {
    background: "linear-gradient(135deg,#ffcc66,#e5ad45)",

    borderColor: "#ffcc66",

    color: "#ffffff",

    boxShadow: "0 0 20px rgba(255,204,102,.3)",
  },

  progressCircleDisabled: {
    opacity: 0.35,
  },

  progressLabel: {
    marginTop: "8px",

    color: "#777e8c",

    fontSize: "10px",

    fontWeight: 700,

    whiteSpace: "nowrap",
  },

  progressLine: {
    height: "2px",

    minWidth: "80px",

    flex: 1,

    background: "#292e39",

    marginTop: "20px",
  },

  progressLineDone: {
    background: "#ffcc66",
  },

  progressLineDisabled: {
    opacity: 0.3,
  },

  qualificationGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",

    gap: "12px",
  },

  timerBox: {
    padding: "20px",

    borderRadius: "17px",

    background:
      "linear-gradient(120deg,rgba(255,204,102,.14),#0c0f15)",

    border: "1px solid rgba(255,204,102,.32)",

    minWidth: 0,
  },

  timerValue: {
    marginTop: "9px",

    fontSize: "26px",

    fontWeight: 900,
  },

  timerMeta: {
    marginTop: "8px",

    color: "#777e8c",

    fontSize: "11px",
  },

  qualifyingStatus: {
    padding: "20px",

    borderRadius: "17px",

    background: "#0c0f15",

    border: "1px solid #1e232e",

    minWidth: 0,
  },

  qualStatusValue: {
    marginTop: "10px",

    fontSize: "20px",

    fontWeight: 850,
  },

  qualSub: {
    color: "#7a808e",

    fontSize: "11px",

    marginTop: "8px",

    lineHeight: 1.6,
  },

  stoppedBox: {
    padding: "25px",

    textAlign: "center",

    borderRadius: "17px",

    background: "rgba(255,77,101,.07)",

    border: "1px solid rgba(255,91,114,.2)",
  },

  stoppedTitle: {
    color: "#ff7187",

    fontSize: "20px",

    fontWeight: 900,
  },

  stoppedText: {
    marginTop: "8px",

    color: "#9a9faa",

    fontSize: "13px",
  },

  claimSection: {
    marginTop: "16px",

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",

    gap: "20px",

    alignItems: "center",

    padding: "24px",

    borderRadius: "22px",

    background:
      "linear-gradient(110deg,rgba(255,204,102,.18),rgba(14,17,24,.98))",

    border: "1px solid rgba(255,204,102,.37)",
  },

  claimLeft: {
    display: "flex",

    flexDirection: "column",

    minWidth: 0,
  },

  nextRewardUsd: {
    fontSize: "37px",

    fontWeight: 950,

    marginTop: "8px",
  },

  kindUsdSmall: {
    color: "#8b919e",
    fontSize: "12px",
    fontWeight: 700,
  },

  nextRewardKind: {
    marginTop: "4px",

    color: "#d3ba86",

    fontSize: "14px",

    fontWeight: 700,
  },

  estimateLabel: {
    marginTop: "8px",

    color: "#6f7582",

    fontSize: "10px",
  },

  claimMiddle: {
    paddingLeft: "10px",

    minWidth: 0,
  },

  priceValue: {
    marginTop: "8px",

    fontSize: "18px",

    fontWeight: 850,
  },

  claimRight: {
    display: "flex",

    flexDirection: "column",

    alignItems: "stretch",

    minWidth: 0,
  },

  claimButton: {
    border: 0,

    borderRadius: "14px",

    padding: "15px 20px",

    color: "#ffffff",

    cursor: "pointer",

    fontWeight: 900,

    fontSize: "13px",

    background: "linear-gradient(135deg,#ffcc66,#e5ad45)",

    boxShadow: "0 10px 30px rgba(255,204,102,.2)",

    width: "100%",
  },

  claimButtonDisabled: {
    opacity: 0.38,

    cursor: "not-allowed",

    boxShadow: "none",
  },

  claimButtonInfo: {
    marginTop: "8px",

    color: "#707684",

    fontSize: "10px",

    textAlign: "center",
  },

  summaryGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",

    gap: "10px",
  },

  message: {
    position: "fixed",

    right: "20px",

    bottom: "20px",

    maxWidth: "420px",

    padding: "14px 17px",

    borderRadius: "13px",

    background: "#161a23",

    border: "1px solid #343a49",

    boxShadow: "0 15px 50px rgba(0,0,0,.45)",

    color: "#ffffff",

    fontSize: "13px",

    zIndex: 100,
  },

  emptyCard: {
    maxWidth: "520px",

    margin: "130px auto",

    padding: "45px",

    borderRadius: "22px",

    textAlign: "center",

    background: "#10131a",

    border: "1px solid #212632",

    color: "#969cab",
  },

  emptyIcon: {
    fontSize: "35px",

    marginBottom: "10px",
  },
};